/**
 * `/settings/issue-types` — the feedback service's vocabulary.
 *
 * The same gap as the activity types': `typeId` is required to open an issue,
 * the mutations existed at the BFF and the service, and no screen reached them.
 *
 * Its mutations take loose arguments rather than an input object, because the
 * feedback service mirrored the CRM's contract without mirroring its GraphQL
 * shape. The frame does not care; the mapping is here, once.
 */
import { useMutation, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { VocabularyScreen } from '../../components/settings/VocabularyScreen.web.jsx';
import {
  CREATE_ISSUE_TYPE,
  ISSUE_TYPES_QUERY,
  SET_ISSUE_TYPE_ACTIVE,
  UPDATE_ISSUE_TYPE,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

export function IssueTypesScreen() {
  const [includeInactive, setIncludeInactive] = useState(true);
  const variables = { includeInactive };
  const { data, loading } = useQuery(ISSUE_TYPES_QUERY, { variables });
  const refetchQueries = [{ query: ISSUE_TYPES_QUERY, variables }];

  const [create, createState] = useMutation(CREATE_ISSUE_TYPE, { refetchQueries });
  const [update, updateState] = useMutation(UPDATE_ISSUE_TYPE, { refetchQueries });
  const [setActive, activeState] = useMutation(SET_ISSUE_TYPE_ACTIVE, { refetchQueries });

  const rows = useMemo(() => data?.issueTypes ?? [], [data]);

  return (
    <VocabularyScreen
      title={S.settings.sections.issueTypes}
      hint={S.settings.issueTypes.hint}
      emptyHint={S.settings.issueTypes.empty}
      createTitle={S.settings.issueTypes.createTitle}
      codePrefix="ISSUE"
      rows={rows}
      loading={loading}
      busy={createState.loading || updateState.loading}
      error={createState.error || updateState.error || activeState.error}
      includeInactive={includeInactive}
      onIncludeInactiveChange={setIncludeInactive}
      onCreate={({ code, labelFa, labelEn, sortOrder }) =>
        create({ variables: { code, labelFa, labelEn, sortOrder } })
      }
      onUpdate={(row, { labelFa, labelEn, sortOrder }) =>
        update({ variables: { id: row.id, labelFa, labelEn, sortOrder } })
      }
      onSetActive={(row, isActive) => setActive({ variables: { id: row.id, isActive } })}
    />
  );
}
